// src/app/core/http/rest.service.ts
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { UserDataService } from '../services/user-data.service';
import { BrowserService } from '../services/browser.service';
import { environment } from '../../../environments/environment';

// Declare the obfuscated getSignature function
declare function getSignature(userName: string): string;

@Injectable({
  providedIn: 'root'
})
export class RestService {
  private apiUrl = environment.apiUrl;

  constructor(
    private http: HttpClient,
    private userDataService: UserDataService,
    private browserService: BrowserService
  ) {}

  private getHeaders(): HttpHeaders {
    let headers = new HttpHeaders();
    
    // Only add headers if we're in browser environment
    if (this.browserService.isBrowser()) {
      const localStorage = this.browserService.getLocalStorage();
      const window = this.browserService.getWindow();
      
      if (localStorage) {
        const authToken = localStorage.getItem('auth_token');
        if (authToken) {
          headers = headers.set('Authorization', `Bearer ${authToken}`);
        }
      }
      
      if (window) {
        // Obfuscated timestamp for Content-MD5
        const ts = window.btoa(window.btoa((new Date()).getTime().toString()));
        headers = headers.set(`${String.fromCharCode(67)}ontent-${String.fromCharCode(77)}D5`, ts);
        
        // Obfuscated signature for Content-AES
        headers = headers.set(`${String.fromCharCode(67)}ontent-${String.fromCharCode(65)}ES`, getSignature(this.userDataService.userName));
      }
    }
    
    return headers;
  }

  get<T>(endPoint: string, params?: Record<string, unknown>): Observable<T> {
    let httpParams = new HttpParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value === null || value === undefined) {
          return;
        }

        if (Array.isArray(value)) {
          value.forEach((item) => {
            if (item === null || item === undefined) {
              return;
            }
            httpParams = httpParams.append(key, String(item));
          });
        } else {
          httpParams = httpParams.set(key, String(value));
        }
      });
    }

    return this.http.get<T>(`${this.apiUrl}${endPoint}`, {
      params: httpParams.keys().length ? httpParams : undefined,
      headers: this.getHeaders()
    });
  }

  post<T>(endPoint: string, body: any, options?: object): Observable<T> {
    return this.http.post<T>(`${this.apiUrl}${endPoint}`, body, { 
      ...(options || {}), 
      observe: 'body' as const,
      headers: this.getHeaders()
    });
  }

  put<T>(endPoint: string, body: any): Observable<T> {
    return this.http.put<T>(`${this.apiUrl}${endPoint}`, body, {
      headers: this.getHeaders()
    });
  }

  delete<T>(endPoint: string, id: string): Observable<T> {
    return this.http.delete<T>(`${this.apiUrl}${endPoint}/${id}`, {
      headers: this.getHeaders()
    });
  }

  getBlob(endPoint: string, params?: any): Observable<Blob> {
    const httpParams = params ? new HttpParams({ fromObject: params }) : undefined;
    return this.http.get(`${this.apiUrl}${endPoint}`, { 
      params: httpParams, 
      responseType: 'blob' as const,
      headers: this.getHeaders()
    });
  }

  isLoading(): boolean {
    // For now, return false. In a real implementation, you would track loading state
    return false;
  }
}